import type {
  TachoCardCounters,
  TachoCardRow,
  TachoCardStopReason,
  TachoDriverCardData,
} from "@/lib/aiWidgetTachoParser"

export type Light = "red" | "amber" | "green" | "off"

const WARSAW_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Warsaw",
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

type Weekdays = Record<string, string>

function warsawParts(iso: string, weekdays: Weekdays) {
  const parts = Object.fromEntries(
    WARSAW_PARTS.formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
  )
  return {
    weekday: weekdays[parts.weekday] ?? parts.weekday,
    date: `${parts.day}.${parts.month}`,
    clock: `${parts.hour}:${parts.minute}`,
  }
}

export const formatClock = (iso: string) => warsawParts(iso, {}).clock

export const formatDay = (iso: string, weekdays: Weekdays) => {
  const { weekday, date } = warsawParts(iso, weekdays)
  return `${weekday} ${date}`
}

export const formatWhen = (iso: string, weekdays: Weekdays) => {
  const { weekday, date, clock } = warsawParts(iso, weekdays)
  return `${weekday} ${date}, ${clock}`
}

export const formatMin = (minutes: number) => {
  const m = Math.max(0, Math.round(minutes))
  if (m === 0) return "0 min"
  if (m < 60) return `${m} min`
  const rest = m % 60
  return `${Math.floor(m / 60)} h${rest ? ` ${String(rest).padStart(2, "0")}` : ""}`
}

export const minutesBetween = (fromIso: string, toIso: string) =>
  Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 60_000)

export const drivingLight = (minutes: number | null): Light =>
  minutes === null
    ? "off"
    : minutes < 60
      ? "red"
      : minutes <= 180
        ? "amber"
        : "green"

export const staleMinutes = (sourceAt: string, now: number) =>
  Math.round((now - Date.parse(sourceAt)) / 60_000)

export const isDayDone = (row: TachoCardRow) =>
  !row.moving && row.remainingMin !== null && row.remainingMin <= 0

export const minutesToStop = (row: TachoCardRow) =>
  row.stopAt && row.sourceAt ? minutesBetween(row.sourceAt, row.stopAt) : null

export function movingRows(rows: TachoCardRow[]) {
  return rows
    .filter((r) => r.moving)
    .sort(
      (a, b) =>
        (a.stopAt ? Date.parse(a.stopAt) : Infinity) -
        (b.stopAt ? Date.parse(b.stopAt) : Infinity),
    )
}

export function standingRows(rows: TachoCardRow[]) {
  const rank = (r: TachoCardRow) =>
    isDayDone(r) || r.remainingMin === null ? -1 : r.remainingMin
  return rows.filter((r) => !r.moving).sort((a, b) => rank(b) - rank(a))
}

export function bindingCounter(
  counters: TachoCardCounters,
): keyof TachoCardCounters | null {
  const candidates = (["todayMin", "weekMin", "twoWeeksMin"] as const).filter(
    (k) => counters[k] !== null,
  )
  if (candidates.length === 0) return null
  return candidates.reduce((best, k) =>
    counters[k]! < counters[best]! ? k : best,
  )
}

export type StopReason = Exclude<TachoCardStopReason, "none">

/** What the driver card says about the next stop. */
export type DriverNextStop =
  | { kind: "stop"; reason: StopReason; at: string; inMin: number }
  | { kind: "left"; min: number }
  | { kind: "exhausted"; counter: keyof TachoCardCounters | null }
  | { kind: "unknown" }
  | { kind: "weeklyRest" }

export function driverNextStop(driver: TachoDriverCardData): DriverNextStop {
  const toStop = minutesToStop(driver)
  if (driver.stopAt && driver.stopReason !== "none" && toStop !== null) {
    return {
      kind: "stop",
      reason: driver.stopReason,
      at: driver.stopAt,
      inMin: toStop,
    }
  }
  // A driver on the weekly rest is not about to drive, whatever the counters say.
  if (driver.rest.inProgress) return { kind: "weeklyRest" }
  if (driver.remainingMin === null) return { kind: "unknown" }
  if (driver.remainingMin <= 0) {
    return { kind: "exhausted", counter: bindingCounter(driver.counters) }
  }
  return { kind: "left", min: driver.remainingMin }
}

/**
 * Driving left before the weekly rest is due, or null when the card cannot
 * say. The week and two-week counters are calendar budgets, so they are capped
 * by today's budget on the last driving day and by the time left until the
 * deadline. A double crew's counters are the pair's combined budget, which is
 * no one driver's time before their rest.
 */
export function drivingBeforeRest(driver: TachoDriverCardData): number | null {
  const { rest, counters } = driver
  if (driver.crew || rest.inProgress || !rest.dueAt) return null
  const limits =
    driver.remainingDaysOfDriving === 0
      ? [driver.remainingMin]
      : [counters.weekMin, counters.twoWeeksMin]
  const known = limits.filter((v): v is number => v !== null)
  if (known.length === 0) return null
  if (driver.sourceAt) known.push(minutesBetween(driver.sourceAt, rest.dueAt))
  return Math.max(0, Math.min(...known))
}
